import { toast } from "sonner";
import { useContext, useId } from "react";
import { Loader2 } from "lucide-react";
import { CrmProvider, CrmFormData } from "../types";
import { trpc } from "../../../trpc";
import type { RouterOutputs } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

type CrmConnection = RouterOutputs["crmSelfConfig"]["get"];
type BitrixMethodKey =
  | "createContact"
  | "createLead"
  | "updateLead"
  | "createDeal"
  | "updateDeal";

type BitrixAuthConfigurationProps = {
  formData: CrmFormData;
  onFormDataChange: (data: Partial<CrmFormData>) => void;
  hasConnection: boolean;
  crmConnection: CrmConnection | undefined;
};

const BITRIX_METHODS = [
  {
    id: "createContact" as const,
    label: "Crear contacto",
    method: "crm.contact.add.json",
    description: "Se usa para crear el contacto cuando el prospecto aún no existe en Bitrix.",
    tokenField: "bitrixCreateContactToken" as const,
    enabledField: "bitrixCreateContactEnabled" as const,
  },
  {
    id: "createLead" as const,
    label: "Crear lead",
    method: "crm.lead.add.json",
    description: "Se usa para crear el lead justo después de crear el contacto.",
    tokenField: "bitrixCreateLeadToken" as const,
    enabledField: "bitrixCreateLeadEnabled" as const,
  },
  {
    id: "updateLead" as const,
    label: "Actualizar lead",
    method: "crm.lead.update.json",
    description:
      "Se usa cuando el lead ya existe en Bitrix (Palomma guardó el ID después de crearlo).",
    tokenField: "bitrixUpdateLeadToken" as const,
    enabledField: "bitrixUpdateLeadEnabled" as const,
  },
  {
    id: "createDeal" as const,
    label: "Crear deal",
    method: "crm.deal.add.json",
    description:
      "Se usa para crear un Deal en Bitrix cuando la configuración del evento usa entidad “Deal”.",
    tokenField: "bitrixCreateDealToken" as const,
    enabledField: "bitrixCreateDealEnabled" as const,
  },
  {
    id: "updateDeal" as const,
    label: "Actualizar deal",
    method: "crm.deal.update.json",
    description:
      "Se usa cuando el Deal ya existe en Bitrix (Palomma guardó el ID después de crearlo).",
    tokenField: "bitrixUpdateDealToken" as const,
    enabledField: "bitrixUpdateDealEnabled" as const,
  },
] satisfies ReadonlyArray<{
  id: BitrixMethodKey;
  label: string;
  method: string;
  description: string;
  tokenField: keyof CrmFormData;
  enabledField: keyof CrmFormData;
}>;

const BitrixAuthConfiguration = ({
  formData,
  onFormDataChange,
  hasConnection,
  crmConnection,
}: BitrixAuthConfigurationProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);
  const utils = trpc.useUtils();

  const createConnection = trpc.crmSelfConfig.create.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.get.invalidate({ merchantId: merchantId || "" });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al crear la conexión.",
      });
    },
  });

  const updateConnection = trpc.crmSelfConfig.updateBitrix.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.get.invalidate({ merchantId: merchantId || "" });
      toast.success("Conexión actualizada", {
        description: "Los cambios a la configuración de Bitrix fueron guardados.",
      });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al actualizar la conexión.",
      });
    },
  });

  const testConnection = trpc.crmSelfConfig.testConnection.useMutation({
    onSuccess: async (result) => {
      if (!result.success) {
        toast.error("Error de conexión", {
          description: result.error || "No se pudo establecer la conexión con Bitrix24.",
        });
        return;
      }

      const baseUrl = (formData.bitrixBaseUrl || crmConnection?.bitrix?.baseUrl || "").trim();
      if (!merchantId || !baseUrl) return;

      const buildMethod = (
        tokenField: typeof BITRIX_METHODS[number]["tokenField"],
        enabledField: typeof BITRIX_METHODS[number]["enabledField"],
        currentToken?: string,
        currentEnabled?: boolean
      ) => {
        const token = (formData[tokenField] || currentToken || "").trim();
        const enabled = formData[enabledField] ?? currentEnabled ?? true;
        return token ? { token, enabled } : undefined;
      };

      const methods = {
        createContact: buildMethod(
          "bitrixCreateContactToken",
          "bitrixCreateContactEnabled",
          crmConnection?.bitrix?.methods?.createContact?.token,
          crmConnection?.bitrix?.methods?.createContact?.enabled
        ),
        createLead: buildMethod(
          "bitrixCreateLeadToken",
          "bitrixCreateLeadEnabled",
          crmConnection?.bitrix?.methods?.createLead?.token,
          crmConnection?.bitrix?.methods?.createLead?.enabled
        ),
        updateLead: buildMethod(
          "bitrixUpdateLeadToken",
          "bitrixUpdateLeadEnabled",
          crmConnection?.bitrix?.methods?.updateLead?.token,
          crmConnection?.bitrix?.methods?.updateLead?.enabled
        ),
        createDeal: buildMethod(
          "bitrixCreateDealToken",
          "bitrixCreateDealEnabled",
          crmConnection?.bitrix?.methods?.createDeal?.token,
          crmConnection?.bitrix?.methods?.createDeal?.enabled
        ),
        updateDeal: buildMethod(
          "bitrixUpdateDealToken",
          "bitrixUpdateDealEnabled",
          crmConnection?.bitrix?.methods?.updateDeal?.token,
          crmConnection?.bitrix?.methods?.updateDeal?.enabled
        ),
      };

      if (hasConnection && crmConnection?.id) {
        await updateConnection.mutateAsync({
          merchantId,
          id: crmConnection.id,
          baseUrl,
          methods,
        });
      } else {
        await createConnection.mutateAsync({
          merchantId,
          provider: "bitrix" as CrmProvider,
          status: "success",
          enabled: true,
          bitrix: {
            baseUrl,
            methods,
          },
        });
      }

      toast.success("Conexión exitosa", {
        description: "La conexión con Bitrix24 se estableció correctamente.",
      });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al probar la conexión.",
      });
    },
  });

  const handleTestConnection = () => {
    const baseUrl = (formData.bitrixBaseUrl || crmConnection?.bitrix?.baseUrl || "").trim();
    const createContactToken = (
      formData.bitrixCreateContactToken ||
      crmConnection?.bitrix?.methods?.createContact?.token ||
      ""
    ).trim();

    if (!baseUrl) {
      toast.warning("Base URL requerida", {
        description: "Ingresa la URL base de tu portal de Bitrix24.",
      });
      return;
    }
    if (!createContactToken) {
      toast.warning("Token requerido", {
        description: "Ingresa al menos el token de crm.contact.add.json para probar la conexión.",
      });
      return;
    }

    testConnection.mutate({
      provider: "bitrix" as CrmProvider,
      baseUrl,
      createContactToken,
      createLeadToken: formData.bitrixCreateLeadToken,
      updateLeadToken: formData.bitrixUpdateLeadToken,
    });
  };

  const isConnected = hasConnection && crmConnection?.status === "success";
  const fieldPrefix = useId();
  const baseUrlId = `${fieldPrefix}-baseUrl`;

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="flex flex-col gap-4">
        <div>
          <div className="mb-3 flex items-center justify-between">
            <Label className="text-lg font-bold text-foreground">Autenticación de Bitrix24</Label>
            {isConnected && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                <span className="size-1.5 rounded-full bg-emerald-500" />
                Conexión exitosa
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Bitrix24 funciona con URLs por método. Pega la URL base de tu portal y el token
            (user+token) de cada método REST que quieras habilitar.
          </p>
        </div>

        <div>
          <Label htmlFor={baseUrlId} className="text-sm font-semibold text-foreground">
            URL base
          </Label>
          <Input
            id={baseUrlId}
            type="text"
            value={formData.bitrixBaseUrl || crmConnection?.bitrix?.baseUrl || ""}
            onChange={(e) => onFormDataChange({ bitrixBaseUrl: e.target.value })}
            placeholder="https://<tu-dominio>.bitrix24.es/rest/<userId>"
            className="bg-muted focus:border-primary focus:bg-card"
            aria-describedby={`${baseUrlId}-help`}
          />
          <p id={`${baseUrlId}-help`} className="mt-1 text-xs text-muted-foreground">
            Ejemplo: <code>https://serviciosintegralesinmobiliarios.bitrix24.es/rest/714</code>
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <Label className="text-sm font-semibold text-foreground">Métodos REST</Label>
          {BITRIX_METHODS.map((method) => {
            const currentToken = formData[method.tokenField];
            const connectionToken = crmConnection?.bitrix?.methods?.[method.id]?.token;
            const enabled = formData[method.enabledField];
            const tokenId = `${fieldPrefix}-${method.id}-token`;
            const descId = `${fieldPrefix}-${method.id}-desc`;

            return (
              <div
                key={method.id}
                className="flex flex-col gap-2 rounded-md border border-border bg-muted p-3"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`${fieldPrefix}-${method.id}-enabled`}
                        checked={enabled}
                        onCheckedChange={(checked) =>
                          onFormDataChange({ [method.enabledField]: checked as boolean })
                        }
                      />
                      <Label
                        htmlFor={`${fieldPrefix}-${method.id}-enabled`}
                        className="text-sm font-semibold text-foreground"
                      >
                        {method.label}
                      </Label>
                      <code className="rounded bg-card px-1.5 py-0.5 text-xs text-muted-foreground ring-1 ring-border">
                        {method.method}
                      </code>
                    </div>
                    <p id={descId} className="ml-6 mt-1 text-xs text-muted-foreground">
                      {method.description}
                    </p>
                  </div>
                </div>
                <div className="ml-6">
                  <Label htmlFor={tokenId} className="sr-only">
                    Token de {method.label}
                  </Label>
                  <Input
                    id={tokenId}
                    type="text"
                    value={currentToken || connectionToken || ""}
                    onChange={(e) =>
                      onFormDataChange({ [method.tokenField]: e.target.value })
                    }
                    placeholder="token del webhook (ej. scm2nfxyfgr5rb8j)"
                    className="bg-card focus:border-primary"
                    aria-describedby={descId}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <Button
          variant="main"
          onClick={handleTestConnection}
          disabled={
            testConnection.isLoading ||
            createConnection.isLoading ||
            updateConnection.isLoading
          }
        >
          {(testConnection.isLoading ||
            createConnection.isLoading ||
            updateConnection.isLoading) && <Loader2 className="size-4 animate-spin" />}
          {isConnected ? "Guardar cambios" : "Probar conexión"}
        </Button>
      </div>
    </div>
  );
};

export default BitrixAuthConfiguration;
